import { ApiError } from '@/lib/types';

// Why the backend refused an authenticated user. The login page explains
// it instead of sending the user straight back to Auth0 (which, with a live
// Google session, would loop).
const REASON_BY_ERROR_CODE = {
  USER_NOT_INVITED: 'not_invited',
  USER_DEACTIVATED: 'deactivated',
} as const;

export type LoginRejectionReason =
  (typeof REASON_BY_ERROR_CODE)[keyof typeof REASON_BY_ERROR_CODE];

const REASONS: readonly string[] = Object.values(REASON_BY_ERROR_CODE);

export const isLoginRejectionReason = (
  value: unknown,
): value is LoginRejectionReason =>
  typeof value === 'string' && REASONS.includes(value);

export const getLoginRejectionReason = (
  status: number | undefined,
  data: unknown,
): LoginRejectionReason | undefined => {
  if (status !== 403) {
    return undefined;
  }
  const code = (data as Partial<ApiError> | undefined)?.error?.code;
  return code && Object.prototype.hasOwnProperty.call(REASON_BY_ERROR_CODE, code)
    ? REASON_BY_ERROR_CODE[code as keyof typeof REASON_BY_ERROR_CODE]
    : undefined;
};

// The refusal plus, when it happened on an invitation link, that
// invitation, so the user can still accept it afterwards.
export type LoginRejection = {
  reason: LoginRejectionReason;
  invitation?: string;
  organization?: string;
};

// Carried across Auth0's logout in sessionStorage, like the post-login
// redirect: `returnTo` must be exactly the registered /login URL, so it
// cannot carry a query string.
const STORAGE_KEY = 'pp.loginRejection';

export const storeLoginRejection = (rejection: LoginRejection) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rejection));
  } catch (error) {
    console.error('Error saving login rejection:', error);
  }
};

const optionalString = (value: unknown) =>
  typeof value === 'string' ? value : undefined;

export const readLoginRejection = (): LoginRejection | undefined => {
  try {
    const stored = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) ?? 'null',
    );
    // Storage can be edited by hand; only a known reason counts.
    if (!isLoginRejectionReason(stored?.reason)) return undefined;
    return {
      reason: stored.reason,
      invitation: optionalString(stored.invitation),
      organization: optionalString(stored.organization),
    };
  } catch (error) {
    console.error('Error reading login rejection:', error);
    return undefined;
  }
};

export const clearLoginRejection = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing login rejection:', error);
  }
};
